import { filter, isEmpty, reject } from 'lodash';

import {
  QUESTION_PROMPT_MAX_LENGTH,
  ANSWER_MAX_LENGTH,
  MAX_ANSWER_LIMIT,
  MAX_RANKING_ANSWER_LIMIT,
  MIN_ANSWER_LIMIT,
  ANSWER_DEFAULT,
  ANSWER_OTHER,
  ANSWER_NONE_OF_THE_ABOVE,
} from 'containers/QuestionnairePage/constants';
import {
  questionSupportsFeature,
  questionAnswerSupportsMedia,
} from 'utils/questionHelpers';
import { FEATURE_ADD_LIMITS, FEATURE_RANKING } from 'data/questionFeatures';
import {
  getAllCarriedForwardCount,
  hasIncompleteCarryForward,
  onlyAnswerSelectionWithPossibleZeroCarryForward,
} from 'containers/QuestionnairePage/NodeSettings/settings/utils/CarryForward';

import {
  isConjointOrMaxDiffGroupNode,
  isSingleQuestionType,
  isRankingQuestionType,
} from 'utils/nodeTypes';
import { getPromptFromHtml } from 'components/FormattedInput/helpers';
import {
  hasLogicVisibilityError,
  isLimitOutOfBoundForVisibility,
} from 'components/VisibilityActionButton/utils';
import { isAnswerUnconditionallyHidden } from 'components/LogicRule/utils';
import {
  hasMissingQuotaFromItems,
  isZeroQuotaAllowed,
  hasWrongQuotaSumFromItems,
  hasWrongMinimumQuotaSumFromItems,
  hasQuotaLessThanCollectedFromItems,
  hasQuotaFilledFromItems,
} from 'utils/quotaHelpers';
import { deriveQuotaMode, QUOTA_MODE } from 'data/quotaModes';
import { surveyStatusEnum } from 'containers/SurveysPage/constants';
import { getScreeningErrors } from 'containers/QuestionnairePage/screening';

import {
  isAnswerEmpty,
  isCarriedForward,
  isAnswerExclusive,
  getPreviousNodeRefIdsForNode,
} from './utils';
import { FORWARD_ANSWER_AS } from '../../NodeSettings/settings/utils/constants';
const validate = (
  {
    refId,
    answers = [],
    prompt,
    min,
    max,
    type,
    isScreening,
    carryForward = [],
    rankingOptions = {},
  },
  nodes = [],
  previousNodesPerNode = {},
  answersWithLogicFromOutcomes = [],
  answersWithLogicFromOutcomesHide = [],
  screeningNodes = [],
  groupNode,
  allNodes,
) => {
  const defaultAnswers = filter(answers, ['type', ANSWER_DEFAULT]);
  const errors = { answerTooLong: {} };
  const answersSupportMedia = questionAnswerSupportsMedia(type);

  if (isEmpty(prompt)) {
    errors.missingPrompt = true;
  }

  const questionTooLong = prompt && prompt.length > QUESTION_PROMPT_MAX_LENGTH;
  if (questionTooLong) {
    errors.questionTooLong = true;
  }

  const carryForwardEnabled = Boolean(carryForward.length);
  const limitAnswersEnabled = Boolean(min || max);
  const hasLimitAnswersEnabled =
    limitAnswersEnabled && questionSupportsFeature(type, FEATURE_ADD_LIMITS);
  const customRankingLabelsEnabled = Boolean(rankingOptions.isEnabled);
  let carriedAnswersCounts = { min: 0, max: 0 };
  if (carryForwardEnabled) {
    carriedAnswersCounts = getAllCarriedForwardCount(carryForward, allNodes)[
      FORWARD_ANSWER_AS.ANSWERS
    ] || { min: 0, max: 0 };
  }

  // multiple answers limit
  if (hasLimitAnswersEnabled) {
    const nonExclusiveOrCFOrUnconditionallyHiddenCount = answers.filter(
      (answer) =>
        !isAnswerExclusive(answer) &&
        !isCarriedForward(answer) &&
        !isAnswerUnconditionallyHidden({
          nodes,
          answersWithLogicFromOutcomesHide,
          item: answer,
        }),
    ).length;
    const availableAnswerRange = {
      min:
        nonExclusiveOrCFOrUnconditionallyHiddenCount + carriedAnswersCounts.min,
      max:
        nonExclusiveOrCFOrUnconditionallyHiddenCount + carriedAnswersCounts.max,
    };

    if (Number.isInteger(min) && Number.isInteger(max)) {
      if (max < min) {
        errors.limitMaxSmaller = true;
      }
      if (
        min <= 0 ||
        max > availableAnswerRange.max ||
        min > availableAnswerRange.min
      ) {
        errors.limitOutOfBounds = true;
      }
    } else {
      errors.limitMissingValues = true;
    }
  }

  if (
    customRankingLabelsEnabled &&
    questionSupportsFeature(type, FEATURE_RANKING)
  ) {
    if (!rankingOptions.low) {
      errors.emptyRankingLow = true;
    }

    if (!rankingOptions.high) {
      errors.emptyRankingHigh = true;
    }
  }

  const nonEmptyAnswers = reject(
    answers,
    (answer) => isAnswerEmpty(answer) || isCarriedForward(answer),
  );

  const validEmptyAnswers = filter(
    defaultAnswers,
    (answer) => isAnswerEmpty(answer) && answer.isEmptyAnswer !== true,
  );
  const emptyOtherAnswer = filter(
    answers,
    (answer) =>
      answer.type === ANSWER_OTHER &&
      (isAnswerEmpty(answer) || answer.label === ''),
  );
  const emptyNoneOfTheAboveAnswer = filter(
    answers,
    (answer) =>
      answer.type === ANSWER_NONE_OF_THE_ABOVE && isAnswerEmpty(answer),
  );

  if (emptyOtherAnswer.length > 0) {
    errors.emptyOtherAnswer = true;
  }
  if (emptyNoneOfTheAboveAnswer.length > 0) {
    errors.emptyNoneOfTheAboveAnswer = true;
  }
  const hasAnswers = nonEmptyAnswers.length >= MIN_ANSWER_LIMIT;
  const belongsToConjointOrMaxDiffGroup =
    isConjointOrMaxDiffGroupNode(groupNode);

  if (!hasAnswers && !carryForwardEnabled && !belongsToConjointOrMaxDiffGroup) {
    errors.missingAnswers = true;
  }

  if (validEmptyAnswers.length > 0) {
    errors.emptyAnswers = true;
  }

  const hasTooManyAnswers = nonEmptyAnswers.length > MAX_ANSWER_LIMIT;
  const hasTooManyRankingAnswers =
    isRankingQuestionType(type) &&
    nonEmptyAnswers.length > MAX_RANKING_ANSWER_LIMIT;

  if (
    !belongsToConjointOrMaxDiffGroup &&
    (hasTooManyAnswers || hasTooManyRankingAnswers)
  ) {
    errors.tooManyAnswers = true;
  }

  if (carryForwardEnabled) {
    const maxLimit = isRankingQuestionType(type)
      ? MAX_RANKING_ANSWER_LIMIT
      : MAX_ANSWER_LIMIT;

    const hasEnoughCFanswers =
      carriedAnswersCounts.min + nonEmptyAnswers.length >= MIN_ANSWER_LIMIT;
    if (!hasEnoughCFanswers) {
      errors.missingAnswersWithCF = true;
    }
    if (nonEmptyAnswers.length + carriedAnswersCounts.max > maxLimit) {
      errors.tooManyAnswersWithCF = true;
    }
    if (!answersSupportMedia) {
      const emptyCarriedAnswers = filter(
        answers,
        (answer) =>
          getPromptFromHtml(answer.body) === '' && isCarriedForward(answer),
      );
      if (emptyCarriedAnswers.length > 0) {
        errors.emptyAnswers = true;
      }
    }

    const hasIncompleteCarryForwardConfigurations = hasIncompleteCarryForward(
      carryForward,
      allNodes,
    );
    if (hasIncompleteCarryForwardConfigurations) {
      errors.incompleteCarryForwardConfiguration = true;
    }

    // find if one of the carry forward nodes is completely skipped
    const previousNodes = getPreviousNodeRefIdsForNode({
      refId,
      previousNodesPerNode,
      nodes,
      screeningNodes,
    });

    const hasUnusedCF = carryForward.some(
      ({ questionRefId }) => !previousNodes.includes(questionRefId),
    );
    if (hasUnusedCF) {
      errors.hasUnusedCF = true;
    }

    if (
      answers.every(isCarriedForward) &&
      onlyAnswerSelectionWithPossibleZeroCarryForward(carryForward)
    ) {
      // errors to show
      errors.onlyAnswerSelectionWithPossibleZeroCarryForward = true;
      // errors to silence
      delete errors.missingAnswersWithCF;
    }
  }

  if (!belongsToConjointOrMaxDiffGroup) {
    // answer prompt max length check
    answers.forEach((answer) => {
      if (getPromptFromHtml(answer.body).length > ANSWER_MAX_LENGTH) {
        errors.answerTooLong[answer.refId] = true;
      }
    });
  }

  if (isEmpty(errors.answerTooLong)) {
    delete errors.answerTooLong;
  }

  if (
    hasLogicVisibilityError({
      nodeType: type,
      isScreening,
      answers: nonEmptyAnswers,
      carriedAnswers: answers.filter(isCarriedForward),
      answersWithLogicFromOutcomes,
    })
  ) {
    errors.hasLogicVisibilityError = true;
  }

  if (
    hasLimitAnswersEnabled &&
    !errors.limitOutOfBounds &&
    !errors.limitMissingValues &&
    isLimitOutOfBoundForVisibility({
      nodeType: type,
      isScreening,
      answers: nonEmptyAnswers,
      answersWithLogicFromOutcomes,
      minLimit: min,
    })
  ) {
    errors.limitOutOfBoundsForVisibility = true;
  }

  return errors;
};

export default validate;

/**
 * Screening answer errors. They read the screener types from the screening
 * rules, so the questionnaire's quota pass, which runs with the rules up to
 * date, is the one that calls this.
 */
export const validateScreeningAnswers = ({ node, screenerTypes }) =>
  node.isScreening
    ? getScreeningErrors({
        question: node,
        screenerTypes,
        isScreenerActive: node.isScreenerActive,
      })
    : {};

/**
 * Answer quota errors, for the survey body and screening alike. Only the
 * visible answers `isEligibleItem` accepts count (on a screening question, the
 * allowed ones). Multiple selection screening quotas aren't validated yet.
 */
export const validateQuotas = (
  node,
  nodeQuotas,
  uncoditionallyHiddenItems,
  surveyConfig,
  surveyStatusName,
  isEligibleItem = () => true,
) => {
  if (node.isScreening && !isSingleQuestionType(node.type)) return {};

  const { isDraft, completes } = surveyConfig;
  const errors = {};
  const isMinimumResponses =
    deriveQuotaMode({
      simpleQuotasPresent: true,
      quotaLimitType: node.quotaLimitType,
    }) === QUOTA_MODE.MIN;

  const isSurveyCompleted = surveyStatusName === surveyStatusEnum.COMPLETED;

  const visibleItemKeys = node.answers
    .map((answer) => answer.refId)
    .filter(
      (refId) =>
        !uncoditionallyHiddenItems.includes(refId) &&
        isEligibleItem({ answerRefId: refId }),
    );

  if (
    !isMinimumResponses &&
    !isSurveyCompleted &&
    hasQuotaFilledFromItems(visibleItemKeys, nodeQuotas.items)
  ) {
    errors.quotasFulfilled = true;
  }
  if (
    hasMissingQuotaFromItems(visibleItemKeys, nodeQuotas.items, {
      allowZeroQuota: isZeroQuotaAllowed({
        isDraft,
        isScreening: node.isScreening,
      }),
      isMinimumResponses,
    })
  ) {
    if (isMinimumResponses) {
      errors.minimumResponsesMissing = true;
    } else {
      errors.quotaMissing = true;
    }
    return errors;
  }

  if (!errors.quotasFulfilled && !isSurveyCompleted) {
    if (isMinimumResponses) {
      if (
        hasWrongMinimumQuotaSumFromItems(
          visibleItemKeys,
          nodeQuotas.items,
          completes,
        )
      ) {
        errors.minimumQuotaSumExceedsCompletes = true;
        return errors;
      }
    } else if (
      hasWrongQuotaSumFromItems(visibleItemKeys, nodeQuotas.items, completes)
    ) {
      errors.quotaSumIsNot100 = true;
      return errors;
    }
  }

  if (
    !isMinimumResponses &&
    hasQuotaLessThanCollectedFromItems(visibleItemKeys, nodeQuotas.items)
  ) {
    errors.hasQuotaLessThanCollected = true;
    return errors;
  }

  return errors;
};
